import { TIMELINE_VIEW } from "../../constants/identifiers";
import interfaceIdentifiers from "../../constants/interfaceIdentifiers";
import { appStateSelectors } from "../../modules/appState";
import { timelineViewSettingsActionTypes } from "../../modules/definition/timelineViewSettings";
import preprocessorQuery from "../../utils/preprocessorQuery";
import {
  getFlashPlaceholder,
  getIconPlaceholder,
  getSwitchViewInstruction,
  getTipIdGenerator,
  getViewNameLabel,
  Placeholders
} from "../utils";
import getSharedTip, { SharedTipIdentifiers } from "./sharedTips";

const isDesktop = preprocessorQuery.isDesktop();
const isTablet = preprocessorQuery.isTablet();
const isMobile = preprocessorQuery.isMobile();

// -----------------------------------------------------
// IMPORTANT NOTES:
//
// GROUP_GATE / accessGate:
//    * expects a function with signature:
//        state => { return true; }
//    * return true to show tutorial
//    * return false to block tutorial and show message
//    * if function is null, will show tutorial
//
// resolve:
//    * expects a function with signature:
//       (action, newState, oldState) => { return true; }
//    * return true to mark the tip as resolved
//    * batch actions will be broken down by the time
//      this function is called, so you will receive the
//      individual actions
//
// -----------------------------------------------------

// Group ID
const GROUP_ID = "GET_COMFORTABLE_WITH_VERSION_3";
const GROUP_LABEL = "Get comfortable with version 3";
const GROUP_DESCRIPTION = `Learn where existing features have moved to in version 3.`;
const GROUP_GATE = null;
const GROUP_GATE_MESSAGE = null;
const GROUP_APPLICABLE_GATE = null;
const GROUP_APPLICABLE_GATE_MESSAGE = null;
const GROUP_COMPLETED_MESSAGE =
  "Great work! Now that you know the basic interface, we will introduce you to some further changes.";
const GROUP_LINKS = [
  {
    label: "Learn how to use splits and filters over group by",
    path: "https://help.timeline.app/article/224-alternatives-to-group-by"
  },
  { label: "User Interface Overview", path: "https://help.timeline.app/article/141-user-interface-overview" },
  {
    label: "Chronological vs Narrative Order",
    path: "https://help.timeline.app/article/149-chronological-vs-narrative-order"
  }
];

// Create an ID generator
// this will make IDs of the form: "GROUP_ID / TIP_ID"
const idGenerator = getTipIdGenerator(GROUP_ID);

let tips = [];
if (isDesktop) {
  tips = [
    {
      id: idGenerator("WHERE_HAVE_ENTITIES_MOVED"),
      label: `See where entities have moved`,
      steps: [
        {
          label: `Open the ${Placeholders.ENTITY_LABEL} panel by clicking its ${Placeholders.ENTITY_ICON_FLASH} ${Placeholders.ENTITY_ICON} in the sidebar.`,
          isNumbered: true
        },
        {
          label: `Click on the ${getFlashPlaceholder(
            interfaceIdentifiers.ENTITY_PANEL_ADD,
            "+ Create button"
          )} to create new ${Placeholders.ENTITY_PLURAL_LABEL}`,
          isNumbered: true
        },
        {
          label: `Click on ${Placeholders.AN_ENTITY_LABEL} name to view and edit details in the Inspector`,
          isNumbered: true
        }
      ],
      accessGate: null,
      gateMessage: null,
      requiresDismissal: true
    },
    {
      id: idGenerator("GROUP_BY_ALTERNATIVE"),
      label: `Alternative for Timeline View Group By`,
      steps: [
        { label: `Open ${getViewNameLabel(TIMELINE_VIEW)} views in two split views`, isNumbered: true },
        { label: `Make the top view the active split`, isNumbered: true },
        { label: `Open the ${Placeholders.ENTITY_LABEL} panel`, isNumbered: true },
        {
          label: `Click on the ${getFlashPlaceholder(
            interfaceIdentifiers.ENTITY_PANEL_FILTER,
            "filter icon"
          )} ${getIconPlaceholder("workviewFilterFilled")} alongside ${Placeholders.AN_ENTITY_LABEL} names`,
          isNumbered: true
        },
        {
          label: `Move focus to the next split view and click the filter icon next to a different ${Placeholders.ENTITY_LABEL}`,
          isNumbered: true
        },
        {
          label: `Each view will show only events related to the selected ${Placeholders.ENTITY_PLURAL_LABEL}`,
          isNumbered: true
        },
        {
          label: `Click the active filter icon ${getIconPlaceholder(
            "workviewFilterFilledActive",
            "iconActive"
          )} again to remove the filter`,
          isNumbered: true
        }
      ],
      accessGate: null,
      gateMessage: null,
      requiresDismissal: true
    },
    {
      id: idGenerator("APPLY_GROUP_BY"),
      label: `Apply "group by" using the old approach`,
      steps: [
        getSwitchViewInstruction(TIMELINE_VIEW),
        {
          label: `In the footer, click on the ${getFlashPlaceholder(
            interfaceIdentifiers.FOOTER_TIMELINE_GROUP_BY,
            "group by icon"
          )} ${getIconPlaceholder("footerTimelineGroupBy")}`,
          isNumbered: true
        },
        { label: "In the popover, choose which entities should become groups", isNumbered: true },
        {
          label: "The timeline view will be divided to show separate groups for each selected entity",
          isNumbered: true
        },
        {
          label: `Click the group by icon again and choose "All Events (No Grouping)" to remove the grouping`,
          isNumbered: true
        }
      ],
      accessGate: null,
      gateMessage: null,
      requiresDismissal: true
    },
    getSharedTip(SharedTipIdentifiers.DRAG_TO_RESIZE, GROUP_ID),
    {
      id: idGenerator("SHOW_MORE_DETAILS"),
      label: `Show more event details`,
      steps: [
        getSwitchViewInstruction(TIMELINE_VIEW),
        {
          label: `Click on the ${getFlashPlaceholder(
            interfaceIdentifiers.FOOTER_CARD_DETAILS,
            "item card icon"
          )} ${getIconPlaceholder("footerCardDetails")} in the footer view`,
          isNumbered: true
        },
        { label: "Use the checkboxes to turn on or off additional item details", isNumbered: true }
      ],
      accessGate: null,
      gateMessage: null,
      resolve: (action, newState) =>
        appStateSelectors.getActiveViewType(newState) === TIMELINE_VIEW &&
        (action.type === timelineViewSettingsActionTypes.CHANGE_SHOW_IDENTIFIER ||
          action.type === timelineViewSettingsActionTypes.CHANGE_SHOW_DATES ||
          action.type === timelineViewSettingsActionTypes.CHANGE_SHOW_DURATION ||
          action.type === timelineViewSettingsActionTypes.CHANGE_SHOW_NARRATIVE ||
          action.type === timelineViewSettingsActionTypes.CHANGE_SHOW_BLOCKS ||
          action.type === timelineViewSettingsActionTypes.CHANGE_SHOW_BLOCKED_BY ||
          action.type === timelineViewSettingsActionTypes.CHANGE_SHOW_IMAGE ||
          action.type === timelineViewSettingsActionTypes.CHANGE_SHOW_SUMMARY ||
          action.type === timelineViewSettingsActionTypes.TOGGLE_SHOW_RELATIONSHIP_TYPES)
    },
    {
      id: idGenerator("LOCATE_VIEW_SETTINGS"),
      label: `Where to find other view settings`,
      steps: [
        getSwitchViewInstruction(TIMELINE_VIEW),
        {
          label: `The footer will show controls to configure the timeline ${getFlashPlaceholder(
            interfaceIdentifiers.FOOTER_TIMELINE_ZOOM,
            "zoom"
          )} and ${getFlashPlaceholder(
            interfaceIdentifiers.FOOTER_TIMELINE_RANGE,
            "range"
          )}, plus many options to control how events are displayed`,
          isNumbered: true
        },
        { label: "Each focused view type will show different footer items relevant to that view", isNumbered: true }
      ],
      accessGate: null,
      gateMessage: null,
      requiresDismissal: true
    }
  ];
} else if (isTablet) {
  tips = [
    {
      id: idGenerator("WHERE_HAVE_ENTITIES_MOVED"),
      label: `See where entities have moved`,
      steps: [
        {
          label: `Open the ${Placeholders.ENTITY_LABEL} panel by tapping its ${Placeholders.ENTITY_ICON_FLASH} ${Placeholders.ENTITY_ICON} in the sidebar.`,
          isNumbered: true
        },
        {
          label: `Tap on the ${getFlashPlaceholder(
            interfaceIdentifiers.ENTITY_PANEL_ADD,
            "+ Create button"
          )} to create new ${Placeholders.ENTITY_PLURAL_LABEL}`,
          isNumbered: true
        },
        {
          label: `Tap on ${Placeholders.AN_ENTITY_LABEL} name to view and edit details in the Inspector`,
          isNumbered: true
        }
      ],
      accessGate: null,
      requiresExitEditMode: true,
      requiresInspector: true,
      gateMessage: null,
      requiresDismissal: true
    },
    {
      id: idGenerator("GROUP_BY_ALTERNATIVE"),
      label: `Alternative for Timeline View Group By`,
      steps: [
        { label: `Open ${getViewNameLabel(TIMELINE_VIEW)} views in two split views`, isNumbered: true },
        { label: `Make the top view the active split`, isNumbered: true },
        { label: `Open the ${Placeholders.ENTITY_LABEL} panel`, isNumbered: true },
        {
          label: `Tap on the ${getFlashPlaceholder(
            interfaceIdentifiers.ENTITY_PANEL_FILTER,
            "filter icon"
          )} ${getIconPlaceholder("workviewFilterFilled")} alongside ${Placeholders.AN_ENTITY_LABEL} names`,
          isNumbered: true
        },
        {
          label: `Move focus to the next split view and tap the filter icon next to a different ${Placeholders.ENTITY_LABEL}`,
          isNumbered: true
        },
        {
          label: `Each view will show only events related to the selected ${Placeholders.ENTITY_PLURAL_LABEL}`,
          isNumbered: true
        },
        {
          label: `Tap the active filter icon ${getIconPlaceholder(
            "workviewFilterFilledActive",
            "iconActive"
          )} again to remove the filter`,
          isNumbered: true
        }
      ],
      accessGate: null,
      gateMessage: null,
      requiresDismissal: true,
      requiresExitEditMode: true,
      requiresInspector: true
    },
    {
      id: idGenerator("SHOW_MORE_DETAILS"),
      label: `Show more event details`,
      steps: [
        getSwitchViewInstruction(TIMELINE_VIEW),
        {
          label: `Tap on the ${getFlashPlaceholder(
            interfaceIdentifiers.IOS_VIEW_DETAILS,
            "view settings icon"
          )} ${getIconPlaceholder("footerCardDetails")} in the Sidebar`,
          isNumbered: true
        },
        {
          label: `In the View Settings panel, use the switches under "Item Display" to turn on or off additional item details`,
          isNumbered: true
        }
      ],
      accessGate: null,
      gateMessage: null,
      requiresInspector: true,
      resolve: (action, newState) =>
        appStateSelectors.getActiveViewType(newState) === TIMELINE_VIEW &&
        (action.type === timelineViewSettingsActionTypes.CHANGE_SHOW_IDENTIFIER ||
          action.type === timelineViewSettingsActionTypes.CHANGE_SHOW_DATES ||
          action.type === timelineViewSettingsActionTypes.CHANGE_SHOW_DURATION ||
          action.type === timelineViewSettingsActionTypes.CHANGE_SHOW_NARRATIVE ||
          action.type === timelineViewSettingsActionTypes.CHANGE_SHOW_BLOCKS ||
          action.type === timelineViewSettingsActionTypes.CHANGE_SHOW_BLOCKED_BY ||
          action.type === timelineViewSettingsActionTypes.CHANGE_SHOW_IMAGE ||
          action.type === timelineViewSettingsActionTypes.CHANGE_SHOW_SUMMARY ||
          action.type === timelineViewSettingsActionTypes.TOGGLE_SHOW_RELATIONSHIP_TYPES)
    },
    {
      id: idGenerator("LOCATE_VIEW_SETTINGS"),
      label: `Where to find other view settings`,
      steps: [
        getSwitchViewInstruction(TIMELINE_VIEW),
        {
          label: `Tap on the ${getFlashPlaceholder(
            interfaceIdentifiers.IOS_VIEW_DETAILS,
            "view settings icon"
          )} ${getIconPlaceholder("footerCardDetails")} in the Sidebar`,
          isNumbered: true
        },
        {
          label: `The view settings panel will show controls to configure the timeline ${getFlashPlaceholder(
            interfaceIdentifiers.FOOTER_TIMELINE_ZOOM,
            "zoom"
          )} plus many options to control how events are arranged`,
          isNumbered: true
        },
        {
          label: "Each focused view type will show different view settings items relevant to that view",
          isNumbered: true
        }
      ],
      accessGate: null,
      gateMessage: null,
      requiresDismissal: true,
      requiresInspector: true
    }
  ];
} else if (isMobile) {
  tips = [];
}

export default {
  id: GROUP_ID,
  label: GROUP_LABEL,
  description: GROUP_DESCRIPTION,
  accessGate: GROUP_GATE,
  gateMessage: GROUP_GATE_MESSAGE,
  applicableGate: GROUP_APPLICABLE_GATE,
  gateApplicableMessage: GROUP_APPLICABLE_GATE_MESSAGE,
  completedMessage: GROUP_COMPLETED_MESSAGE,
  links: GROUP_LINKS,
  tips
};
